export type Variant = "min" | "avg" | "max";

export type PositionCategory = "budowlana" | "ppn" | "biuro" | "inne";

export const CATEGORY_LABELS: Record<PositionCategory, string> = {
  budowlana: "Budowlana",
  ppn: "PPN",
  biuro: "Biuro",
  inne: "Inne",
};

export interface CategoryShare {
  value: string;
  pct: string;
}

export interface EstimateAnalytics {
  /** Suma w wariancie złożonym jako oferta (job.bid_variant). */
  total: string;
  /** Ten sam sumaryczny wynik pod historyczną nazwą klucza. */
  total_avg: string;
  /** Wariant, z którego policzono te wskaźniki. */
  variant?: Variant;
  route_length_m: string | null;
  route_open_trench_m: string | null;
  route_bore_m: string | null;
  line_rate_per_m: string | null;
}

export interface AsyncTaskRecord {
  id: string;
  status: "pending" | "running" | "success" | "failure" | "cancelled";
  log: string;
  result: unknown;
  error: string;
}

export interface VariantTotals {
  sum_R: string;
  sum_M: string;
  sum_S: string;
  direct: string;
  kp: string;
  z: string;
  kz: string;
  total: string;
}

export type Totals = Record<Variant, VariantTotals> & {
  coefficients?: Record<string, string>;
};

export interface PositionComputed {
  R: string;
  M: string;
  S: string;
  direct: string;
  kp: string;
  z: string;
  kz: string;
  value: string;
  /** Simplified method only: BCJ unit price for the variant. */
  price?: string;
}

export interface RateCatalogEntry {
  id: string;
  kind: string;
  code: string;
  normalized_code: string;
  description: string;
  unit: string;
  price_avg: string | null;
  price_min: string | null;
  price_max: string | null;
}

export interface MatchSuggestion {
  entry_id: string;
  code: string;
  description: string;
  /** jm pozycji cennika — cena „za m” przy nakładzie w szt. ma być widoczna
   *  zanim ktoś kliknie podpowiedź. Opcjonalna: podpowiedzi zapisane przed
   *  dodaniem tego pola jej nie mają. */
  unit?: string;
  price_avg: string | null;
  score: number;
}

export interface AiPriceSource {
  title: string;
  url: string;
  snippet: string;
}

export interface AiPriceFields {
  ai_price: string | null;
  ai_price_min: string | null;
  ai_price_max: string | null;
  ai_price_confidence: string;
  ai_price_basis: string;
  ai_price_sources: AiPriceSource[];
}

export interface PositionInput extends AiPriceFields {
  id: string;
  kind: "labor" | "material" | "equipment";
  description: string;
  /** Nazwa z dokumentu sprzed ręcznej zmiany (puste = nazwa bez zmiany). */
  description_original: string;
  factor_code: string;
  unit: string;
  norm_per_unit: string;
  norm_total: string;
  /** Nakład z dokumentu sprzed ręcznej zmiany ilości (null = bez zmiany). */
  norm_total_original: string | null;
  is_percent_aux: boolean;
  /** Nakład dopisany ręcznie pod pozycją dodaną ręcznie (można go usunąć). */
  is_manual: boolean;
  investor_supplied: boolean;
  /** Nakład wykreślony przez kosztorysanta — widoczny, ale liczony po 0 zł. */
  excluded: boolean;
  manual_price: string | null;
  match_status: "exact" | "fuzzy" | "ai" | "ai_price" | "manual" | "unmatched";
  match_score: number;
  matched_entry: string | null;
  matched_code: string | null;
  matched_description: string | null;
  matched_price_avg: string | null;
  suggestions: MatchSuggestion[];
  unit_price: Partial<Record<Variant, string>>;
  subtotal: Partial<Record<Variant, string>>;
  /** Komentarz kosztorysanta do tego nakładu (wolny tekst, może być pusty). */
  comment: string;
}

/** Pozycja dopisana ręcznie: „kompleksowa” = kwota końcowa, „markup” = + Kp i Z,
 *  „inputs” = bez ceny jednostkowej, wyceniana z dopisanych nakładów R/M/S. */
export type ManualPricingMode = "complex" | "markup" | "inputs";

export const MANUAL_MODE_LABELS: Record<ManualPricingMode, string> = {
  complex: "Kompleksowa — kwota końcowa (bez narzutów)",
  markup: "Dolicz koszty pośrednie (Kp) i zysk (Z)",
  inputs: "Z nakładów R/M/S (materiał / robocizna / sprzęt)",
};

/** One R/M/S line struck out during norm expansion (recorded, not priced). */
export interface StrippedNormInput {
  kind: "labor" | "material" | "equipment";
  description: string;
  unit: string;
  norm_total: string;
  is_percent_aux?: boolean;
}

/** Warning recorded while expanding a przedmiar position from KNR/KNNR norms,
 *  e.g. demontaż priced with a montaż norm ("analogia") → materials struck. */
export interface ExpansionWarning {
  code:
    | "demontaz_analogia"
    | "device_mismatch"
    | "unit_mismatch"
    | "unit_scale_mismatch"
    | "unit_unknown"
    | string;
  norm_code: string;
  norm_desc: string;
  note: string;
  stripped: StrippedNormInput[];
  /** Tylko `device_mismatch`: rodzina urządzenia z opisu pozycji vs z normy. */
  position_group?: string;
  position_group_label?: string;
  norm_group?: string;
  norm_group_label?: string;
  /** `unit_mismatch` (nakładów nie rozwinięto), `unit_scale_mismatch` (obmiar
   *  przeliczony na jm normy), `unit_unknown` (nie było czym skontrolować). */
  norm_unit?: string;
  position_unit?: string;
  /** Tylko `unit_scale_mismatch`: ile jm normy przypada na 1 jm pozycji
   *  („0.001” dla obmiaru w m przy normie na km) i obmiar po przeliczeniu. */
  ratio?: string;
  converted_quantity?: string;
}

export interface Position extends AiPriceFields {
  id: string;
  ordinal: number;
  /** Lp jak w dokumencie ("1.1"); puste = numeracja własna z `ordinal`. */
  ordinal_label: string;
  section: string;
  basis_raw: string;
  basis_catalog: string;
  normalized_code: string;
  description: string;
  /** Opis z dokumentu sprzed ręcznej zmiany nazwy (puste = nazwa bez zmiany). */
  description_original: string;
  unit: string;
  quantity: string;
  /** Obmiar z dokumentu sprzed ręcznej zmiany (null = obmiar bez zmiany). */
  quantity_original: string | null;
  obmiar_formula: string;
  krotnosc: string;
  match_status: string;
  /** Dopasowana pozycja cennika (BCJ) albo — przy pozycji ręcznej — pozycja
   *  „Naszej bazy cen”, z której ją wstawiono. */
  matched_work_entry: string | null;
  category: PositionCategory;
  category_source: string;
  category_confidence: number;
  computed: Partial<Record<Variant, PositionComputed>>;
  expansion_warnings: ExpansionWarning[];
  /** Komentarz kosztorysanta do pozycji (np. „cenę zaniżyłem świadomie”). */
  comment: string;
  /** Dostawa inwestorska całej pozycji (działa też przy nakładach — zeruje całą). */
  investor_supplied: boolean;
  /** Kategoria gruntu ustawiona przełącznikiem (puste = nieustawiona). */
  soil_category: string;
  /** Kategoria gruntu efektywna: pole albo odczyt z opisu pozycji. */
  effective_soil_category: string;
  /** Narzuty pozycji ryczałtowej: jak materiał (tylko Kz) vs jak robocizna (Kp+Z). */
  whole_price_markup: "material" | "labor";
  /** Pozycja dopisana ręcznie (nie z dokumentu) — wyceniana z manual_price. */
  is_manual: boolean;
  /** Cena jednostkowa wpisana ręcznie (pozycja ręczna albo nadpisanie wyceny
   *  pozycji bez nakładów); ujemna = korekta w dół. */
  manual_price: string | null;
  manual_pricing_mode: ManualPricingMode | "";
  inputs: PositionInput[];
}

export interface EstimateResult {
  id: string;
  document: string;
  title: string;
  page_range: string;
  method: "simplified" | "detailed";
  method_display: string;
  naklady_source: "" | "document" | "norms";
  naklady_source_display: string;
  currency: string;
  valuation: Totals;
  warnings: string[];
  route_length_m: string | null;
  route_open_trench_m: string | null;
  route_bore_m: string | null;
  route_length_source: string;
  route_length_note: string;
  category_breakdown: Partial<Record<PositionCategory, CategoryShare>>;
  analytics: EstimateAnalytics | Record<string, never>;
  positions: Position[];
}

export interface DocumentPage {
  id: string;
  page_number: number;
  is_kosztorys_nakladczy: boolean;
  classification: string;
  classification_display: string;
  confidence: number;
  note: string;
}

export interface EstimateDocument {
  id: string;
  filename: string;
  page_count: number;
  has_estimate: boolean;
  /** ManagedFile id — świeży link do pobrania przez GET /files/{id}/. */
  managed_file: string | null;
  pages: DocumentPage[];
}

export interface CoefficientSet {
  id: string;
  name: string;
  kp_pct: string;
  z_pct: string;
  kz_pct: string;
  kp_base: string;
  z_base: string;
  kz_base: string;
  r_rate_avg: string;
  r_rate_min: string | null;
  r_rate_max: string | null;
  branch: string;
  is_default: boolean;
}

export interface RateCatalog {
  id: string;
  kind: "bcj" | "rms";
  kind_display: string;
  source_label: string;
  quarter: string;
  entry_count: number;
  is_active: boolean;
  /** „Nasza baza cen” — ręcznie utrzymywany katalog użytkowników (singleton). */
  is_custom: boolean;
}

/** Rozkład zawartości cennika — nagłówek przeglądarki cenników. */
export interface RateCatalogStats {
  entry_count: number;
  by_kind: { kind: string; kind_display: string; count: number }[];
  /** Ile pozycji ma w ogóle cenę średnią. */
  priced: number;
  /** Ile pozycji ma widełki (cenę minimalną). */
  with_range: number;
}

// ── Katalogi norm KNR/KNNR (przeglądarka) ────────────────────────────────────

export interface NormCatalogHeader {
  id: string;
  source_label: string;
  note: string;
  position_count: number;
  is_active: boolean;
}

/** Księga katalogu („KNNR 5”) z licznikami. `coded` = ile wariantów ma nadany
 *  kod — reszta jest w katalogu, tylko bez kodu (cross-referencja z BCJ jej nie
 *  objęła), więc trafia się do nich przez tablicę, a nie przez kod. */
export interface NormFamily {
  catalog_code: string;
  group: string;
  positions: number;
  tables: number;
  coded: number;
}

export interface NormTable {
  catalog_code: string;
  table_title: string;
  variants: number;
  /** Numer tablicy odczytany z kodu wariantu ("0701"); pusty, gdy kodów brak. */
  table_code: string;
}

/** Jeden nakład R/M/S — norma NA JEDNOSTKĘ pozycji. */
export interface NormItem {
  id: string;
  kind: "labor" | "material" | "equipment" | "work_unit";
  kind_display: string;
  description: string;
  unit: string;
  norm: string;
  /** „Materiały pomocnicze X%” — norma jest procentem, nie ilością. */
  is_percent_aux: boolean;
  factor_sl: number;
}

export interface NormPosition {
  id: string;
  catalog: string;
  catalog_code: string;
  code: string;
  normalized_code: string;
  description: string;
  table_title: string;
  variant_index: number;
  unit: string;
  kat_file: string;
  soil_category: string;
  item_count: number;
}

export interface NormVariantRef {
  id: string;
  normalized_code: string;
  description: string;
  variant_index: number;
  unit: string;
  soil_category: string;
  is_current: boolean;
}

export interface NormBcjPrice {
  catalog_id: string;
  catalog_label: string;
  quarter: string;
  code: string;
  description: string;
  unit: string;
  price_min: string | null;
  price_avg: string | null;
  price_max: string | null;
}

export interface NormPositionDetail extends NormPosition {
  items: NormItem[];
  variants: NormVariantRef[];
  bcj_prices: NormBcjPrice[];
}

/** Odpowiedź stronicowana — `count` z meta, żeby dało się pokazać „x z N”. */
export interface Paged<T> {
  results: T[];
  count: number;
}

/** Pozycja „Naszej bazy cen” — ręcznie zweryfikowana cena nietypowej pozycji,
 *  dołączana do dopasowań obok cennika SEKOCENBUD. */
export interface CustomPriceEntry {
  id: string;
  /** work_unit = „pozycja główna” (cała usługa/robota, np. nadzór archeologiczny) —
   *  wstawiana ręcznie do kosztorysu; material/equipment = pojedynczy nakład. */
  kind: "material" | "equipment" | "work_unit";
  code: string;
  description: string;
  unit: string;
  price_avg: string | null;
  price_min: string | null;
  price_max: string | null;
  note: string;
  source_url: string;
  created_by_name: string;
  created_at: string | null;
}

export interface FrameworkAgreementOption {
  id: string;
  title: string;
  contractor_name: string;
  line_item_count: number;
  /** Grupy umów ramowych (AgreementGroup); pusta lista = „Bez przypisanej grupy". */
  groups: { id: string; name: string }[];
}

export interface ScenarioLineItem {
  id: string;
  name: string;
  unit: string;
  base_rate: string;
  high_priority_rate: string | null;
  contractor_name: string;
  agreement_title: string;
}

export interface AssignmentCoverage {
  id: string;
  position: string;
  position_ordinal: number;
  position_description: string;
  /** Wartość CAŁEJ pozycji u nas (wariant złożony jako oferta). */
  position_value: string;
  /** Kwota naszej wartości przypadająca na to pokrycie = wartość × udział. */
  amount: string;
  fraction: string;
}

export interface SubcontractorAssignment {
  id: string;
  contractor: string | null;
  contractor_name: string | null;
  line_item_type: string | null;
  description: string;
  unit: string;
  quantity: string;
  /** Ilości nie da się wyprowadzić z obmiaru (cennik w innej jednostce, np. szt. vs m). */
  quantity_needs_review: boolean;
  quantity_note: string;
  /** Powód czerwonego wykrzyknika przy ilości ("" = ilość w porządku). */
  quantity_alert: string;
  rate: string;
  use_high_priority: boolean;
  is_alternative: boolean;
  tier_step: string | null;
  tier_subsequent_rate: string | null;
  is_tiered: boolean;
  value_multiplier: string | null;
  travel_free_km: string | null;
  match_status: string;
  match_score: number;
  ai_rationale: string;
  note: string;
  cost: string;
  coverages: AssignmentCoverage[];
}

export interface AlternativeOffer {
  id: string;
  description: string;
  contractor_name: string | null;
  cost: string;
  covered_ordinals: number[];
}

export interface SubCostSnapshot {
  subcontractor_total: string;
  operator_total_all: string;
  operator_covered: string;
  uncovered_value: string;
  coverage_pct: string;
  margin: string;
  margin_pct: string;
  by_contractor: { contractor_id: string; name: string; cost: string }[];
  by_category: Partial<
    Record<PositionCategory, { operator: string; sub: string; margin: string }>
  >;
  /** Zakres, którego nie bierze żaden podwykonawca. `partial` = pozycja jest
   *  podzielona i to jest RESZTA, która zostaje u nas (value = ta reszta). */
  uncovered_positions: {
    ordinal: number;
    description: string;
    value: string;
    partial?: boolean;
  }[];
  alternatives: AlternativeOffer[];
  /** Ile przypisań czeka na ręczne uzupełnienie ilości (niezgodne jednostki). */
  needs_quantity?: number;
  warnings: string[];
}

export interface SubcontractorScenarioLite {
  id: string;
  name: string;
  is_primary: boolean;
  cost_snapshot: SubCostSnapshot | Record<string, never>;
}

export interface SubcontractorScenario extends SubcontractorScenarioLite {
  job: string;
  created_at: string;
  agreement_ids: string[];
  assignments: SubcontractorAssignment[];
  build_label: string;
  build_lat: string | null;
  build_lng: string | null;
  travel_snapshot: Record<string, { km: number; base: string; name: string }>;
  ai_rule_text: string;
}

export interface ContractorTravelInfo {
  id: string;
  name: string;
  registered_city: string;
  bases: { id: string; label: string; city: string }[];
  travel_km: number | null;
  travel_base: string | null;
}

export type ReviewStatus = "ok" | "warning" | "error";

// One row of the structured review checklist (one review "question"/category).
export interface ReviewCheck {
  category: string;
  label: string;
  status: ReviewStatus;
  summary: string;
  ordinals: number[];
  recommendation: string;
}

export interface AiReview {
  summary: string;
  checks: ReviewCheck[];
  advice: string[];
  counts: Record<ReviewStatus, number>;
  /** Niepuste, gdy podsumowanie wymienia problemy nieodzwierciedlone w tabeli
   *  statusów — audyt jest wtedy wewnętrznie sprzeczny i nie należy mu ufać. */
  consistency_alert?: string;
  model: string;
  generated_at: string;
}

/** Wynik zmiany podstawy / kategorii gruntu (meta.rebase z endpointu). */
export interface RebaseResult {
  norm_code: string;
  norm_desc: string;
  inputs: number;
  matched_inputs: number;
  unmatched_inputs: number;
  warnings: string[];
  soil_category: string;
  old_soil_category?: string;
}

/** Wariant tablicy KNNR podpowiadany przy nieznanym kodzie (errors.variants). */
export interface NormVariant {
  code: string;
  description: string;
  unit: string;
  soil_category: string;
}

/** Raport zbiorczej zmiany kategorii gruntu (meta.soil). */
export interface SoilReport {
  target: string;
  changed: { position: string; lp: string; norm_code: string; norm_desc: string; unmatched_inputs: number }[];
  skipped: { position: string; lp: string; reason: string }[];
  /** Wspólny identyfikator wpisów dziennika tej kaskady (null, gdy nic nie zmieniono). */
  batch_id: string | null;
}

/** Stan pozycji w podglądzie „przed / po” zmiany kategorii gruntu. */
export interface SoilPositionState {
  basis_raw: string;
  soil_category: string;
  /** Wartość pozycji (wariant średni); null przed pierwszym przeliczeniem. */
  value: string | null;
  /** Nakłady sprzętowe (opisy maszyn) — dobór sprzętu przed/po. */
  equipment: string[];
}

export interface SoilPreviewChange {
  position: string;
  lp: string;
  norm_code: string;
  norm_desc: string;
  unmatched_inputs: number;
  description: string;
  old: SoilPositionState | null;
  new: SoilPositionState;
}

/** Dry-run kaskady (meta.soil_preview) — nic nie zostało zapisane. */
export interface SoilPreview {
  target: string;
  /** Wariant, w którym pokazane są kwoty (wariant oferty zlecenia). */
  variant?: Variant;
  changed: SoilPreviewChange[];
  skipped: { position: string; lp: string; reason: string }[];
  totals: { old: string | null; new: string | null };
}

/** Wpis dziennika zmian podstawy/kategorii gruntu (change-log). */
export interface ChangeLogEntry {
  id: string;
  position: string;
  position_lp: string;
  position_description: string;
  kind: "basis" | "soil";
  kind_display: string;
  /** Wspólny dla wpisów jednej kaskady; null = zmiana pojedyncza. */
  batch_id: string | null;
  changed_at: string;
  changed_by_name: string;
  old_basis_raw: string;
  new_basis_raw: string;
  old_normalized_code: string;
  new_normalized_code: string;
  old_soil_category: string;
  new_soil_category: string;
  old_inputs_count: number;
  reverted_at: string | null;
  reverted_by_name: string;
}

/** Raport wycofania zmiany (meta.revert). */
export interface RevertReport {
  reverted: { position: string; lp: string; basis_raw: string; soil_category: string; inputs: number }[];
  skipped: { position: string; lp: string; reason: string }[];
}

/** Podpowiedź z katalogu norm (typeahead w edytorze podstawy). */
export interface NormSearchHit {
  code: string;
  description: string;
  unit: string;
  soil_category: string;
  table_title: string;
}

/** Jak skończył się przetarg, do którego liczona była wycena (backend: TenderOutcome). */
export type TenderOutcome = "won" | "lost" | "cancelled";

/** Etykiety wyniku — te same, co wysyła backend w status_display/outcome_display. */
export const OUTCOME_LABELS: Record<TenderOutcome, string> = {
  won: "Przetarg wygrany",
  lost: "Przegrane",
  cancelled: "Anulowane",
};

export interface EstimateJobList {
  id: string;
  status: "new" | "analyzing" | "ready" | "failed";
  status_display: string;
  /** Skąd wzięły się pozycje: wgrany plik czy narysowana trasa. Kształt pozycji i cały
   *  dalszy potok są identyczne — to pole służy do nazwania rzeczy po imieniu. */
  source: "upload" | "takeoff";
  /** Jawnie kliknięte GOTOWE — bez tego analiza „ready” to „W przygotowaniu”. */
  is_completed: boolean;
  /** Oferta faktycznie złożona w przetargu — status „Złożony” (nadpisuje „Gotowy”). */
  is_submitted: boolean;
  /** Decyzja „Nie startujemy” — nie składamy oferty. Nadpisuje każdy inny status. */
  is_abandoned: boolean;
  /** Wynik przetargu — stan KOŃCOWY wyceny, nadpisuje wszystkie pozostałe statusy.
   *  null = przetarg jeszcze nierozstrzygnięty. */
  outcome: TenderOutcome | null;
  /** Kiedy zapisano wynik przetargu (null gdy go nie ma). */
  outcome_at: string | null;
  /** Joined title(s) of the kosztorys(es) in this job ("" when none). */
  title: string;
  /** Termin złożenia oferty — wpisywany ręcznie (ISO, null gdy nieznany). */
  offer_deadline: string | null;
  /** Link do przetargu — wklejany ręcznie ("" gdy brak). */
  tender_url: string;
  totals_snapshot: Partial<Totals>;
  /** Który wariant wartości przetargowej składamy jako ofertę. */
  bid_variant: Variant;
  analytics: EstimateAnalytics | Record<string, never>;
  estimate_count: number;
  created_by_name: string;
  shared_with_names: string[];
  is_shared_with_me: boolean;
  created_at: string;
}

export interface EstimateJobDetail extends EstimateJobList {
  created_by_name: string;
  /** Wariant rysunku, z którego wygenerowano przedmiar (tylko source = "takeoff"). */
  ze_variant_id: string | null;
  /** Czego reguły obmiaru nie pokryły — patrz TakeoffReportCard. */
  takeoff_report: TakeoffReport;
  shared_with: { id: string; name: string; can_edit: boolean }[];
  /** Autor albo CEO — może udostępniać dalej i usunąć wycenę. */
  can_share: boolean;
  /** Może zmieniać wycenę: autor, CEO albo udostępnienie z prawem edycji. */
  can_edit: boolean;
  completed_at: string | null;
  submitted_at: string | null;
  /** Kiedy zapadła decyzja „Nie startujemy” (null = startujemy / brak decyzji). */
  abandoned_at: string | null;
  /** Kwota FAKTYCZNIE złożona w przetargu — stemplowana wariantem przy oznaczeniu
   *  „Złożony”, potem ręcznie edytowalna. null poza statusem „Złożony” (i na
   *  wycenach złożonych, zanim to pole powstało — wtedy czytamy wariant). */
  submitted_amount: string | null;
  /** Etykieta wyniku przetargu ("" gdy nierozstrzygnięty). */
  outcome_display: string;
  /** Notatka kosztorysanta do przetargu (co uwzględnić / dokończyć). */
  note: string;
  rms_catalog: string | null;
  bcj_catalog: string | null;
  coefficient_set: string | null;
  coefficients_snapshot: Record<string, string>;
  category_breakdown: Partial<Record<PositionCategory, CategoryShare>>;
  analytics: EstimateAnalytics | Record<string, never>;
  ai_review: AiReview | Record<string, never>;
  /** Warunki wyłączeń zasilania wyciągnięte ze specyfikacji ({} gdy nie szukano). */
  outages: JobOutages | Record<string, never>;
  /** Wykaz dostawy inwestorskiej ze specyfikacji ({} gdy nie szukano). */
  investor_supply: JobInvestorSupply | Record<string, never>;
  /** Weryfikacja wyceny z dokumentacją projektową ({} gdy nie sprawdzano). */
  device_check: JobDeviceCheck | Record<string, never>;
  ze_tender: string | null;
  ai_model: string;
  ai_input_tokens: number;
  ai_output_tokens: number;
  ai_calls: number;
  ai_cost_usd: string;
  files: {
    id: string;
    original_filename: string;
    kind: string;
    document_count: number;
    managed_file: string | null;
  }[];
  documents: EstimateDocument[];
  estimates: EstimateResult[];
}

/** Baza wiedzy PRZ-ARON — prosta notatka tekstowa ucząca AI doboru/wyceny pozycji. */
export interface KnowledgeNote {
  id: string;
  body: string;
  is_active: boolean;
  created_by_name: string;
  created_at: string;
  updated_at: string;
}

// ── Zestawienie materiałów / sprzętu (liczone automatycznie z nakładów) ──────

/** Skąd wzięła się cena jednostkowa użyta w wycenie dla tego wiersza. */
export type PriceSource =
  | "catalog"
  | "manual"
  | "ai"
  | "investor"
  | "mixed"
  | "none";

/** Najtańsza oferta znaleziona przez AI dla jednego materiału.
 *  UWAGA: NIE wpływa na wartość oferty przetargowej — to wyłącznie informacja
 *  zakupowa (ile możemy zaoszczędzić, jeśli kupimy taniej). */
export interface MaterialOffer {
  status: "found" | "not_found" | "error";
  unit_price: string | null;
  vendor: string;
  url: string;
  delivery_note: string;
  confidence: string;
  basis: string;
  sources: AiPriceSource[];
  /** (cena z wyceny − cena z oferty) × obmiar; „0” gdy taniej nie znaleziono. */
  saving: string;
  saving_pct: string;
  /** Kosztorys zmienił się po skanie (inny obmiar lub inna cena bazowa). */
  stale: boolean;
  scanned_at: string;
}

export interface SummaryRow {
  key: string;
  description: string;
  unit: string;
  quantity: string;
  /** Cena jednostkowa (śr.) — ważona, gdy ten sam materiał wyceniono dwojako. */
  unit_price: string | null;
  value: Record<Variant, string>;
  positions: number[];
  position_count: number;
  investor_supplied: boolean;
  is_percent_aux: boolean;
  /** Pozycja wyceniona w całości (ryczałt „jak materiał”) — bez nakładów. */
  is_whole_position: boolean;
  matched_code: string;
  price_source: PriceSource;
  /** Jednostka to motogodziny (m-g) — dotyczy zestawienia sprzętu. */
  is_motohours: boolean;
  offer?: MaterialOffer | null;
}

export interface SummaryTotals {
  count: number;
  value: Record<Variant, string>;
  /** Suma R/M/S z samej wyceny — kontrolnie, musi się zgadzać z tabelą. */
  valuation_sum: string;
  motohours: string;
}

export interface JobSummaries {
  materials: { rows: SummaryRow[]; totals: SummaryTotals };
  equipment: { rows: SummaryRow[]; totals: SummaryTotals };
  savings: {
    scanned_rows: number;
    rows_with_saving: number;
    total: string;
    pct_of_materials: string;
    checked_value: string;
  };
  /** Domyślne zaznaczenie do rozeznania rynku: ilość × cena > próg. */
  default_keys: string[];
  threshold: string;
}

// ── Wyłączenia zasilania (ze specyfikacji przetargowej) ──────────────────────

export interface OutageCitation {
  page: number;
  /** Dosłowny cytat z dokumentu — bez parafrazy. */
  quote: string;
}

export interface OutageDocumentFinding {
  filename: string;
  planned: "yes" | "no" | "unknown";
  max_hours_per_outage: string | null;
  total_hours: string | null;
  max_outages: number | null;
  notice_days: string | null;
  window: string;
  penalty: string;
  note: string;
  citations: OutageCitation[];
  model: string;
}

export interface JobOutages {
  status: "planned" | "not_planned" | "unknown";
  /** Dozwolony czas JEDNEGO wyłączenia [h] — najostrzejszy z dokumentów. */
  max_hours_per_outage: string | null;
  total_hours: string | null;
  max_outages: number | null;
  notice_days: string | null;
  window: string;
  penalty: string;
  note: string;
  /** Dokumenty podają różne limity — w nagłówku jest najostrzejszy. */
  conflict: boolean;
  documents: OutageDocumentFinding[];
  scanned_documents: number;
  model: string;
  generated_at: string;
}

// ── Dostawa inwestorska zadeklarowana w specyfikacji ─────────────────────────

/** Nakład kosztorysu powiązany z pozycją wykazu — wyceniony na 0 zł. */
export interface InvestorSupplyMatch {
  input_id: string;
  description: string;
  quantity: string;
  unit: string;
  /** Lp pozycji kosztorysu, w której siedzi nakład. */
  position: string;
  reason: string;
}

/** Jedna pozycja wykazu „dostawa inwestorska obejmuje…" ze specyfikacji. */
export interface InvestorSupplyItem {
  name: string;
  quantity: string | null;
  unit: string;
  page: number;
  /** Dosłowny cytat z dokumentu — podstawa wyzerowania. */
  quote: string;
  filename: string;
  matched?: InvestorSupplyMatch[];
  /** Ustawione, gdy ilość z deklaracji nie zgadza się z ilością w kosztorysie. */
  quantity_note?: string;
}

export interface JobInvestorSupply {
  declared: "yes" | "no" | "unknown";
  items: InvestorSupplyItem[];
  note: string;
  documents: {
    filename: string;
    declared: string;
    items: number;
    note: string;
  }[];
  /** Ile nakładów kosztorysu powiązano z wykazem. */
  matched_inputs: number;
  scanned_documents: number;
  model: string;
  generated_at: string;
}

// ── Weryfikacja z dokumentacją projektową (urządzenia) ───────────────────────

/** Jedno urządzenie odczytane z projektu (opis techniczny / schemat / rysunek). */
export interface DeviceRegisterEntry {
  filename: string;
  name: string;
  /** Oznaczenie z dokumentu, przepisane dosłownie: "P3", "ZK-1a", "SZR 250A". */
  type: string;
  quantity: string | null;
  unit: string;
  action: "montaz" | "demontaz" | "istniejace" | "przebudowa";
  pages: number[];
  source: string;
  /** Dosłowny cytat / etykieta ze schematu, na podstawie której wpisano pozycję. */
  quote: string;
}

export type DeviceFindingKind =
  | "not_in_docs"
  | "missing_in_estimate"
  | "type_mismatch"
  | "quantity_mismatch";

/** Rozjazd między dokumentacją a wyceną. Nie zmienia kwot — wymaga decyzji. */
export interface DeviceFinding {
  kind: DeviceFindingKind;
  label: string;
  severity: "error" | "warning" | "info";
  /** Lp pozycji wyceny; null gdy ustalenie nie dotyczy konkretnej pozycji. */
  ordinal: number | null;
  device: string;
  in_estimate: string;
  in_docs: string;
  quote: string;
  note: string;
  recommendation: string;
}

export interface DeviceCheckDocument {
  filename: string;
  pages_read: number[];
  device_count: number;
  note: string;
}

export interface JobDeviceCheck {
  devices: DeviceRegisterEntry[];
  documents: DeviceCheckDocument[];
  scanned_documents: number;
  summary: string;
  findings: DeviceFinding[];
  counts: { error: number; warning: number; info: number };
  /** false = rejestr powstał, ale porównania z wyceną nie udało się wykonać. */
  checked: boolean;
  model: string;
  generated_at: string;
}

// ── obmiar z rysunku: słownik reguł ─────────────────────────────────────────

/** Co reguła robi, gdy trafi na pasujący element rysunku. */
export type TakeoffRuleKind = "position" | "check";

/** Operatory warunku. Wartości muszą się zgadzać z backendem (takeoff_expr.CONDITION_OPS). */
export type TakeoffConditionOp = "eq" | "ne" | "gt" | "gte" | "lt" | "lte" | "in";

export interface TakeoffCondition {
  key: string;
  op: TakeoffConditionOp;
  value: string | number | boolean | string[];
}

export interface TakeoffRule {
  id: string;
  kind: TakeoffRuleKind;
  element_type: string;
  element_type_code: string;
  element_type_label: string;
  element_type_kind: "line" | "object" | "area";
  /** Wszystkie muszą być spełnione (AND). Pusto = każdy element tego typu. */
  conditions: TakeoffCondition[];
  section: string;
  /** Puste dozwolone — pozycję bez podstawy dopasuje AI wyceny. */
  basis: string;
  description: string;
  unit: string;
  quantity_expr: string;
  category: string;
  requires_element_type: string | null;
  requires_element_type_label: string | null;
  warning: string;
  order: number;
  is_active: boolean;
  note: string;
  /** Zmienne, których wolno użyć we wzorze tej reguły — żeby nie trzeba było zgadywać nazw. */
  available_variables: string[];
  created_by_name: string;
  created_at: string;
  updated_at: string;
}

export interface TakeoffRuleFormData {
  kind?: TakeoffRuleKind;
  element_type: string;
  conditions?: TakeoffCondition[];
  section?: string;
  basis?: string;
  description?: string;
  unit?: string;
  quantity_expr?: string;
  category?: string;
  requires_element_type?: string | null;
  warning?: string;
  order?: number;
  is_active?: boolean;
  note?: string;
}

export interface TakeoffVariables {
  geometry: Record<string, string>;
  condition_ops: Record<string, string>;
  element_types: {
    element_type: string;
    code: string;
    label: string;
    kind: "line" | "object" | "area";
    unit: string;
    variables: string[];
    param_schema: {
      key: string;
      label: string;
      type: "number" | "choice" | "bool";
      unit?: string;
      default?: string | number | boolean | null;
      choices?: { value: string; label: string }[];
      help?: string;
    }[];
  }[];
}

/** Co reguły ominęły — odpowiedź na „żeby o niczym nie zapomnieć". */
export interface TakeoffReport {
  positions?: number;
  uncovered?: {
    element_type: string;
    label: string;
    features: number;
    /** true = reguły dla tego typu są, ale żadna nie pasuje do parametrów. */
    has_rules: boolean;
  }[];
  checks?: string[];
  errors?: string[];
}
